import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { formatCurrency } from '@/lib/currency';
import { SidebarNav } from '@/pages/AccountsPage';
import {
  Plus, LogOut, Menu, X, Target, Pencil, Trash2, AlertTriangle,
  CheckCircle2, TrendingDown, Wallet
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const monthLabel = (ym, locale = 'es-ES') => {
  if (!ym || !/^\d{4}-\d{2}$/.test(ym)) return ym || '';
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1, 1);
  const s = d.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const offsetMonth = (ym, delta) => {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${yy}-${mm}`;
};

const todayYM = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const BudgetsPage = () => {
  const { user, logout, isAdmin, currency, locale } = useAuth();
  const navigate = useNavigate();
  const fmt = (v) => formatCurrency(v, currency, locale);

  const [budgets, setBudgets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [month, setMonth] = useState(todayYM());

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ category: '', amount: '' });

  const fetchBudgets = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/budgets?month=${month}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setBudgets(await res.json());
    } finally {
      setLoading(false);
    }
  }, [month]);

  const fetchCategories = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setCategories(await res.json());
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchBudgets(); }, [fetchBudgets]);
  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const expenseCategories = useMemo(
    () => categories.filter(c => c.type === 'expense' && c.is_active !== false).map(c => c.name),
    [categories]
  );

  // Categories that still don't have a budget for this month
  const budgetedNames = useMemo(() => new Set(budgets.map(b => b.category)), [budgets]);
  const availableCategories = useMemo(
    () => expenseCategories.filter(n => !budgetedNames.has(n) || (editing && editing.category === n)),
    [expenseCategories, budgetedNames, editing]
  );

  // KPIs
  const kpis = useMemo(() => {
    const total = budgets.reduce((s, b) => s + (b.amount || 0), 0);
    const spent = budgets.reduce((s, b) => s + (b.spent || 0), 0);
    const exceeded = budgets.filter(b => b.is_exceeded).length;
    const warning = budgets.filter(b => !b.is_exceeded && (b.percentage || 0) >= 80).length;
    return {
      total,
      spent,
      remaining: Math.max(0, total - spent),
      excess: Math.max(0, spent - total),
      percentage: total > 0 ? (spent / total) * 100 : 0,
      exceeded,
      warning
    };
  }, [budgets]);

  const openCreate = () => {
    setEditing(null);
    setForm({ category: '', amount: '' });
    setDialogOpen(true);
  };

  const openEdit = (budget) => {
    setEditing(budget);
    setForm({ category: budget.category, amount: String(budget.amount) });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.category || !form.amount) return;
    const amt = parseFloat(form.amount);
    if (isNaN(amt) || amt <= 0) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/budgets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          category: form.category,
          amount: amt,
          month
        })
      });
      if (res.ok) {
        setDialogOpen(false);
        setEditing(null);
        setForm({ category: '', amount: '' });
        fetchBudgets();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (budget) => {
    if (!window.confirm(`¿Eliminar el presupuesto de "${budget.category}" para ${monthLabel(month, locale)}?`)) return;
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_URL}/api/budgets/${budget.id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    });
    if (res.ok) fetchBudgets();
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  // Generate a small range of months for the selector (12 back, 3 ahead)
  const monthOptions = useMemo(() => {
    const now = todayYM();
    const out = [];
    for (let i = -12; i <= 3; i++) out.push(offsetMonth(now, i));
    return out;
  }, []);

  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col lg:flex-row">
      {/* Mobile drawer */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-label="Menú">
          <div className="fixed inset-0 bg-black/60" onClick={() => setMobileMenuOpen(false)} />
          <aside className="fixed left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-zinc-900 border-r border-zinc-800 p-6 z-50 overflow-y-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-2xl font-black gradient-text">WSFinances</h1>
                <p className="text-zinc-500 text-sm mt-1">Panel de Finanzas</p>
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="text-zinc-300 p-2 -mr-2" aria-label="Cerrar menú">
                <X className="h-6 w-6" />
              </button>
            </div>
            <SidebarNav active="budgets" isAdmin={isAdmin} onClick={() => setMobileMenuOpen(false)} />
            <div className="mt-8 pt-6 border-t border-zinc-800">
              <Button variant="ghost" className="w-full justify-start text-zinc-300 hover:text-red-400" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" /> Cerrar Sesión
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside className="w-64 sidebar border-r border-zinc-800 p-6 hidden lg:block relative min-h-screen">
        <div className="mb-8">
          <h1 className="text-2xl font-black gradient-text">WSFinances</h1>
          <p className="text-zinc-500 text-sm mt-1">Panel de Finanzas</p>
        </div>
        <SidebarNav active="budgets" isAdmin={isAdmin} />
        <div className="absolute bottom-6 left-6 right-6">
          <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700 mb-4">
            <p className="text-sm text-zinc-400">Conectado como</p>
            <p className="font-medium text-white truncate">{user?.name}</p>
          </div>
          <Button variant="ghost" className="w-full justify-start text-zinc-300 hover:text-red-400" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" /> Cerrar Sesión
          </Button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 overflow-x-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button className="lg:hidden text-zinc-300 p-2 -ml-2" onClick={() => setMobileMenuOpen(true)} aria-label="Abrir menú">
            <Menu className="h-6 w-6" />
          </button>
          <div className="min-w-0 flex-1">
            <h2 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
              <Target className="h-7 w-7 text-violet-400" />
              Presupuestos
            </h2>
            <p className="text-zinc-400 text-sm">
              Define un límite mensual por categoría y mira cómo lo estás cumpliendo.
            </p>
          </div>
          <Button
            onClick={openCreate}
            className="bg-violet-600 hover:bg-violet-700 shrink-0"
            data-testid="add-budget-btn"
            disabled={availableCategories.length === 0}
          >
            <Plus className="h-4 w-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Nuevo presupuesto</span>
            <span className="sm:hidden">Nuevo</span>
          </Button>
        </div>

        {/* Month selector */}
        <Card className="bg-zinc-900/40 border-zinc-800 mb-6">
          <CardContent className="p-4 flex flex-wrap items-center gap-3">
            <Label className="text-zinc-300 text-sm" htmlFor="month-select">Mes:</Label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger id="month-select" className="bg-zinc-950 border-zinc-800 w-56" data-testid="month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {monthOptions.map(m => (
                  <SelectItem key={m} value={m}>{monthLabel(m, locale)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="ml-auto flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setMonth(offsetMonth(month, -1))}
                className="border-zinc-700 hover:bg-zinc-800 text-zinc-300"
                data-testid="month-prev"
              >
                ◄ Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setMonth(todayYM())}
                className="border-zinc-700 hover:bg-zinc-800 text-zinc-300"
                data-testid="month-today"
              >
                Hoy
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setMonth(offsetMonth(month, 1))}
                className="border-zinc-700 hover:bg-zinc-800 text-zinc-300"
                data-testid="month-next"
              >
                Siguiente ►
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* KPI cards */}
        {budgets.length > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
            <Card className="bg-zinc-900/50 border-zinc-800" data-testid="kpi-total">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-zinc-400 text-xs mb-1">
                  <Target className="h-3.5 w-3.5" /> Total presupuestado
                </div>
                <p className="text-xl sm:text-2xl font-bold text-white">{fmt(kpis.total)}</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900/50 border-zinc-800" data-testid="kpi-spent">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-zinc-400 text-xs mb-1">
                  <TrendingDown className="h-3.5 w-3.5" /> Gastado
                </div>
                <p className="text-xl sm:text-2xl font-bold text-white">{fmt(kpis.spent)}</p>
                <p className="text-xs text-zinc-500 mt-1">
                  {kpis.percentage.toFixed(0)}% del total
                </p>
              </CardContent>
            </Card>
            <Card className={`bg-zinc-900/50 border-zinc-800 ${kpis.excess > 0 ? 'border-red-500/40' : ''}`} data-testid="kpi-remaining">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-zinc-400 text-xs mb-1">
                  <Wallet className="h-3.5 w-3.5" />
                  {kpis.excess > 0 ? 'Excedido por' : 'Restante'}
                </div>
                <p className={`text-xl sm:text-2xl font-bold ${kpis.excess > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                  {fmt(kpis.excess > 0 ? kpis.excess : kpis.remaining)}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900/50 border-zinc-800" data-testid="kpi-alerts">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-zinc-400 text-xs mb-1">
                  <AlertTriangle className="h-3.5 w-3.5" /> Alertas
                </div>
                <p className="text-xl sm:text-2xl font-bold text-white">
                  <span className="text-red-400">{kpis.exceeded}</span>
                  <span className="text-zinc-500 text-base"> / </span>
                  <span className="text-yellow-400">{kpis.warning}</span>
                </p>
                <p className="text-xs text-zinc-500 mt-1">excedidos / cerca del límite</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Budgets grid */}
        {loading ? (
          <p className="text-zinc-500">Cargando presupuestos…</p>
        ) : budgets.length === 0 ? (
          <Card className="bg-gradient-to-br from-violet-500/10 to-pink-500/5 border-violet-500/30" data-testid="empty-state">
            <CardContent className="p-10 text-center">
              <div className="inline-flex h-14 w-14 rounded-full bg-violet-500/20 items-center justify-center mb-4">
                <Target className="h-7 w-7 text-violet-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                Sin presupuestos para {monthLabel(month, locale)}
              </h3>
              <p className="text-zinc-400 mb-6 max-w-md mx-auto">
                Define cuánto quieres gastar este mes por categoría. Te avisaremos cuando te acerques al límite y cuando lo excedas.
              </p>
              <Button
                onClick={openCreate}
                className="bg-violet-600 hover:bg-violet-700"
                disabled={availableCategories.length === 0}
                data-testid="empty-create-budget-btn"
              >
                <Plus className="h-4 w-4 mr-2" /> Crear primer presupuesto
              </Button>
              {availableCategories.length === 0 && (
                <p className="text-xs text-zinc-500 mt-3">
                  Crea categorías de gasto en{' '}
                  <button onClick={() => navigate('/categories')} className="text-violet-400 hover:underline">
                    /categories
                  </button>
                  {' '}para poder presupuestar.
                </p>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="budgets-grid">
            {budgets.map((b) => {
              const pct = b.percentage || 0;
              const remaining = Math.max(0, (b.amount || 0) - (b.spent || 0));
              const excess = Math.max(0, (b.spent || 0) - (b.amount || 0));
              const status = b.is_exceeded ? 'exceeded' : pct >= 80 ? 'warning' : 'ok';
              const barColor = status === 'exceeded' ? 'bg-red-500' : status === 'warning' ? 'bg-yellow-500' : 'bg-emerald-500';
              const ringColor = status === 'exceeded' ? 'border-red-500/40' : status === 'warning' ? 'border-yellow-500/30' : 'border-zinc-800';
              const textColor = status === 'exceeded' ? 'text-red-400' : status === 'warning' ? 'text-yellow-400' : 'text-emerald-400';

              return (
                <Card key={b.id} className={`bg-zinc-900/50 ${ringColor}`} data-testid={`budget-card-${b.category}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3 gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-bold text-white truncate" title={b.category}>{b.category}</p>
                        <p className={`text-xs ${textColor}`}>
                          {status === 'exceeded' && <span className="flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Excedido</span>}
                          {status === 'warning' && <span className="flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Cerca del límite</span>}
                          {status === 'ok' && <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> En camino</span>}
                        </p>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-zinc-400 hover:text-violet-300"
                          onClick={() => openEdit(b)}
                          aria-label={`Editar presupuesto de ${b.category}`}
                          data-testid={`edit-budget-${b.category}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-zinc-400 hover:text-red-400"
                          onClick={() => handleDelete(b)}
                          aria-label={`Eliminar presupuesto de ${b.category}`}
                          data-testid={`delete-budget-${b.category}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="w-full bg-zinc-800 rounded-full h-2.5 mb-3 overflow-hidden">
                      <div
                        className={`h-2.5 ${barColor} transition-all`}
                        style={{ width: `${Math.min(pct, 100)}%` }}
                      />
                    </div>

                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-zinc-300">{fmt(b.spent)}</span>
                      <span className="text-zinc-500">/ {fmt(b.amount)}</span>
                    </div>

                    <div className="flex justify-between items-center pt-3 border-t border-zinc-800">
                      <span className={`text-2xl font-bold ${textColor}`}>{pct.toFixed(0)}%</span>
                      <div className="text-right">
                        {excess > 0 ? (
                          <>
                            <p className="text-xs text-zinc-500">Excedido por</p>
                            <p className="text-sm font-bold text-red-400">{fmt(excess)}</p>
                          </>
                        ) : (
                          <>
                            <p className="text-xs text-zinc-500">Te queda</p>
                            <p className="text-sm font-bold text-emerald-400">{fmt(remaining)}</p>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* Create / edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle>
              {editing ? `Editar presupuesto` : 'Nuevo presupuesto'}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {monthLabel(month, locale)}
              {editing && <span className="block text-xs mt-1">Categoría: {editing.category}</span>}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="budget-category">Categoría</Label>
              <Select
                value={form.category}
                onValueChange={(v) => setForm({ ...form, category: v })}
                disabled={!!editing}
              >
                <SelectTrigger id="budget-category" className="bg-zinc-800 border-zinc-700" data-testid="budget-category-trigger">
                  <SelectValue placeholder="Selecciona una categoría" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {availableCategories.length === 0 && !editing ? (
                    <div className="px-2 py-3 text-sm text-zinc-400">
                      Todas tus categorías ya tienen presupuesto este mes.
                    </div>
                  ) : (
                    availableCategories.map(name => (
                      <SelectItem key={name} value={name}>{name}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="budget-amount">Límite mensual ({currency})</Label>
              <Input
                id="budget-amount"
                type="number"
                step="0.01"
                min="0.01"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
                placeholder="0.00"
                data-testid="budget-amount-input"
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-zinc-300">
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-violet-600 hover:bg-violet-700"
                disabled={saving || !form.category || !form.amount}
                data-testid="save-budget-btn"
              >
                {saving ? 'Guardando…' : editing ? 'Actualizar' : 'Crear presupuesto'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BudgetsPage;
